import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  OnInit,
  signal,
  computed,
  inject,
  DestroyRef,
  ChangeDetectorRef,
  ViewEncapsulation,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PermissionService } from '@app/core/services/permission.service';

import {
  FormFieldConfig,
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { ActionButton } from '@shared/components/status-filter/status-filter.component';
import { ConfirmationButton } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  RolesSidebarComponent,
  RoleSidebarModel,
} from '../roles-sidebar/roles-sidebar.component';
import { RolesService } from '../../services/roles.service';
import { take } from 'rxjs';
import { KeycloakGroupResponse } from '@app/api/user-service/model/keycloakGroupResponse';
import {
  RoleStoreService,
  SelectedSidebarItem,
} from '../../services/rolestore.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AddRoleDialogComponent } from '../add-role-dialog/add-role-dialog.component';
import {
  ALL_ROLES_COLUMNS,
  ALL_ROLES_DEFAULT_COLUMNS_ORDER,
  ALL_ROLES_FLEXIBLE_COLUMNS_ORDER,
  ALL_ROLES_ACTION_COLUMN,
  CHILD_ROLES_COLUMNS,
  CHILD_ROLES_DEFAULT_COLUMNS_ORDER,
  CHILD_ROLES_FLEXIBLE_COLUMNS_ORDER,
  CHILD_ROLES_ACTION_COLUMN,
} from '../../roles.config';
import { PermissionTreeComponent } from '../permission-tree/permission-tree.component';

// Extended interface to include statusFlag for KeycloakGroupResponse
interface ExtendedKeycloakGroupResponse extends KeycloakGroupResponse {
  statusFlag?: boolean;
}

const ROLE_MANAGEMENT_PERMISSION_PREFIX =
  'settings.user.roles_and_permissions_management';

export interface RoleModel {
  id?: number;
  name: string;
  description?: string;
  code?: string;
  statusFlag?: boolean;
  permissions?: string[];
  userCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface DeleteRoleTarget {
  name: string;
}

@Component({
  selector: 'app-roles-list',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ConfirmDialogModule,
    ToastModule,
    TranslateModule,
    AppButtonComponent,
    SearchFormComponent,
    RolesSidebarComponent,
    ConfirmationDialogComponent,
    PermissionTreeComponent,
  ],
  providers: [MessageService, ConfirmationService, DialogService],
  templateUrl: './roles-list.component.html',
  styleUrls: ['./roles-list.component.css'],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RolesListComponent implements OnInit {
  @ViewChild('allRolesTable') allRolesTable?: AdvancedDataTableComponent;
  @ViewChild('childRolesTable') childRolesTable?: AdvancedDataTableComponent;
  @ViewChild('permissionTree') permissionTree?: PermissionTreeComponent;

  // Pagination
  rows = 20;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private messageService = inject(MessageService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private rolesService = inject(RolesService);
  private roleStoreService = inject(RoleStoreService);
  private dialogRef: DynamicDialogRef | null = null;
  private readonly dialogService = inject(DialogService);

  constructor(private translateService: TranslateService) {}

  // Permission flags - computed from PermissionService
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      `${ROLE_MANAGEMENT_PERMISSION_PREFIX}.view`,
    );
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      `${ROLE_MANAGEMENT_PERMISSION_PREFIX}.create`,
    );
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      `${ROLE_MANAGEMENT_PERMISSION_PREFIX}.edit`,
    );
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      `${ROLE_MANAGEMENT_PERMISSION_PREFIX}.delete`,
    );
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction(
      `${ROLE_MANAGEMENT_PERMISSION_PREFIX}.approve`,
    );
  });

  // Check if user has any action permissions
  hasAnyActionPermission = computed(
    () => this.canUpdate() || this.canApprove() || this.canDelete(),
  );

  // Configuration
  searchConfig: FormFieldConfig[] = [];
  permissionSearchConfig: FormFieldConfig[] = [];

  // Advanced table column configuration for all roles
  allRolesDefaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      ALL_ROLES_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of ALL_ROLES_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(ALL_ROLES_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  allRolesFlexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      ALL_ROLES_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of ALL_ROLES_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Advanced table column configuration for child roles
  childRolesDefaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      CHILD_ROLES_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of CHILD_ROLES_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(CHILD_ROLES_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  childRolesFlexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      CHILD_ROLES_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of CHILD_ROLES_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Button configuration
  private readonly primaryButtonBase: ActionButton = {
    label: 'common.actions.new',
    severity: 'info',
    show: true,
    icon: 'add',
  };

  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  secondaryButton = computed(() => {
    const selected = this.selectedRoles();
    return {
      label: this.translateService.instant('common.actions.delete'),
      icon: 'pi pi-trash',
      show: this.canDelete() && selected.length > 0,
    };
  });

  // Data signals
  roles = signal<ExtendedKeycloakGroupResponse[]>([]);
  loading = signal(false);
  totalRecords = signal(0);
  selectedRoles = signal<ExtendedKeycloakGroupResponse[]>([]);
  selectedRole = signal<SelectedSidebarItem | null>(null);
  selectedRoleMenuCodes = signal<string[]>([]);
  hasSelectedRole = computed(() => !!this.selectedRole()?.name);
  breadcrumb = signal<string[]>(['all_roles']);
  activeTabIndex = signal<number>(0); // 0: Child roles, 1: Members, 2: Permissions
  selectedRolePermissions = signal<
    Array<{ displayName: string; description: string }>
  >([]);

  // Child roles data for tab 0
  childRoles = signal<ExtendedKeycloakGroupResponse[]>([]);
  childRolesLoading = signal(false);
  childRolesTotalRecords = signal(0);
  childRolesSearch = signal('');
  childRolesSearchConfig: FormFieldConfig[] = [];
  childRolesPage = signal(0);
  childRolesSize = signal(20);

  permissionSearchTerm = signal('');

  // Search parameters
  searchParams = signal({
    name: '',
    code: '',
    statusFlag: undefined as boolean | undefined,
    selectedTreeItem: '', // Selected tree item from sidebar
    page: 0,
    size: 20,
  });

  // Signal to clear sidebar selection
  clearSidebarSelection = signal(false);

  // Flag to track if we're in tree selection mode
  isTreeSelectionMode = signal(false);

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedRoleForAction: DeleteRoleTarget | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'delete' | 'batchDelete' | 'statusChange' | null = null;

  // Loading states for different operations
  deleteLoading = signal(false);
  statusUpdateLoading = signal(false);

  ngOnInit(): void {
    this.initializeSearchConfig();
    this.initializePermissionSearchConfig();
    this.initializeChildRolesSearchConfig();

    // Load roles from the shared store
    this.roleStoreService.loadRoles();

    // Subscribe to loading state from store
    this.roleStoreService.loading$.subscribe((isLoading) => {
      this.loading.set(!!isLoading);
      this.cdr.detectChanges();
    });

    // Subscribe to processed table data from store
    this.roleStoreService.tableData$.subscribe((tableData: any) => {
      const data: any[] = Array.isArray(tableData) ? tableData : [];
      const extendedRoles = data.map((role: any) => ({
        ...role,
        statusFlag: role?.statusFlag ?? false,
      }));
      this.roles.set(extendedRoles);
      this.totalRecords.set(extendedRoles.length);
      this.cdr.detectChanges();
    });

    // Track selected role changes from sidebar/table via store
    this.roleStoreService.selectedSidebarItem$.subscribe((item) => {
      this.selectedRole.set(item);
      this.loadSelectedRoleData(item?.name);
      this.updateBreadcrumb();
      this.updateSelectedRolePermissions();
      this.activeTabIndex.set(0);
      this.childRoles.set([]);
      this.childRolesTotalRecords.set(0);
      this.cdr.detectChanges();
    });

    // Also need original data for breadcrumb resolution
    this.roleStoreService.originalData$.subscribe(() => {
      this.updateBreadcrumb();
      this.updateSelectedRolePermissions();
      this.cdr.detectChanges();
    });
  }

  onSavePermissions(menuCodes: string[]) {
    if (!this.canUpdate()) {
      return;
    }

    const roleName = this.selectedRole()?.name;
    if (!roleName) {
      this.messageService.add({
        severity: 'warn',
        summary: 'warning',
        detail: 'Vui lòng chọn role',
      });
      return;
    }

    const payload = {
      name: roleName,
      menuCodes: [...new Set(menuCodes)],
    };

    // call service and subscribe so the HTTP request is executed
    this.rolesService
      .updateRole({
        roleName,
        payload: payload as any,
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'configuration.roles.messages.update_role_success',
            ),
          });
        },
        error: (err) => {
          console.error('Error saving permissions', err);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'configuration.roles.messages.update_role_failed',
            ),
          });
        },
      });
  }

  private loadSelectedRoleData(roleName?: string): void {
    if (!roleName) {
      this.selectedRoleMenuCodes.set([]);
      return;
    }

    this.rolesService
      .getRoleDataByName(roleName)
      .pipe(take(1))
      .subscribe((role) => {
        const menuCodes = this.toMenuCodeArray(role?.menuCodes);

        this.selectedRoleMenuCodes.set(menuCodes);
        this.cdr.detectChanges();
      });
  }

  private toMenuCodeArray(menuCodes?: Set<string> | string[] | null): string[] {
    if (!menuCodes) {
      return [];
    }

    return Array.isArray(menuCodes)
      ? menuCodes.filter(Boolean)
      : Array.from(menuCodes).filter(Boolean);
  }

  // Drill-down: clicking role name in table selects role (does not alter sidebar expand state)
  onDrillDown(row: ExtendedKeycloakGroupResponse): void {
    const item = {
      id: row.id || '',
      name: row.name || '',
      path: row.path,
      parentId: row.parentId,
    } as any;
    this.roleStoreService.setSelectedSidebarItem(item);
  }

  // Breadcrumb navigation handler
  navigateToBreadcrumb(index: number): void {
    if (index === 0) {
      // All roles
      this.roleStoreService.resetToParentOnly();
      return;
    }
    // Reconstruct path up to index and find node
    const item = this.selectedRole();
    if (!item || !item.path) return;
    const segments = item.path.split('/').filter(Boolean);
    const targetSegments = segments.slice(0, index);
    let all: KeycloakGroupResponse[] | null = null;
    const sub = this.roleStoreService.originalData$.subscribe(
      (data) => (all = data),
    );
    sub.unsubscribe();
    if (!all) return;
    const target = this.findNodeByPath(all, targetSegments);
    if (target) {
      this.roleStoreService.setSelectedSidebarItem({
        id: target.id || '',
        name: target.name || '',
        path: target.path,
        parentId: target.parentId,
      });
    }
  }

  // Build breadcrumb like: All roles > parent > child
  private updateBreadcrumb(): void {
    const item = this.selectedRole();
    const crumbs: string[] = [];
    crumbs.push('all_roles');
    if (!item || !item.path) {
      this.breadcrumb.set(crumbs);
      return;
    }
    const segments = (item.path || '').split('/').filter(Boolean);
    const names = segments.length ? segments : item.name ? [item.name] : [];
    this.breadcrumb.set(['all_roles', ...names]);
  }

  // Compute permissions of selected role for Permissions tab (read-only)
  private updateSelectedRolePermissions(): void {
    const item = this.selectedRole();
    let all: KeycloakGroupResponse[] | null = null;
    const sub = this.roleStoreService.originalData$.subscribe(
      (data) => (all = data),
    );
    sub.unsubscribe();
    if (!item || !all) {
      this.selectedRolePermissions.set([]);
      return;
    }
    const node = this.findNodeById(all, item.id);
    if (!node || !node.realmRoles) {
      this.selectedRolePermissions.set([]);
      return;
    }
    const rolesArray =
      node.realmRoles instanceof Set
        ? Array.from(node.realmRoles as any)
        : (node.realmRoles as any[]);
    const view = rolesArray
      .filter((r) => r && r.name && /^\d+$/.test(r.name))
      .map((r: any) => ({
        displayName: this.rolesService.permissionIdToLabel(r.name),
        description: r.description || '',
      }));
    this.selectedRolePermissions.set(view);
  }

  private findNodeById(
    nodes: KeycloakGroupResponse[],
    id?: string | null,
  ): KeycloakGroupResponse | null {
    if (!id) return null;
    for (const n of nodes) {
      if (n.id === id) return n;
      if (n.subGroups && n.subGroups.length) {
        const found = this.findNodeById(n.subGroups, id);
        if (found) return found;
      }
    }
    return null;
  }

  private findNodeByPath(
    nodes: KeycloakGroupResponse[],
    segments: string[],
  ): KeycloakGroupResponse | null {
    if (!segments.length) return null;
    const [head, ...tail] = segments;
    for (const n of nodes) {
      if (n.name === head) {
        if (tail.length === 0) return n;
        if (n.subGroups && n.subGroups.length) {
          const found = this.findNodeByPath(n.subGroups, tail);
          if (found) return found;
        }
      }
    }
    return null;
  }

  private initializeSearchConfig(): void {
    this.searchConfig = [
      {
        key: 'code',
        label: 'Search permission',
        type: 'text',
        placeholder: 'Search permission',
      },
    ];
  }

  private initializePermissionSearchConfig(): void {
    this.permissionSearchConfig = [
      {
        key: 'permissionName',
        label: 'configuration.roles.search.permission_name',
        type: 'text',
        width: '50%',
      },
    ];
  }

  private initializeChildRolesSearchConfig(): void {
    this.childRolesSearchConfig = [
      {
        key: 'search',
        label: this.translateService.instant('configuration.roles.search.code'),
        type: 'text',
        placeholder: this.translateService.instant(
          'configuration.roles.search.code_placeholder',
        ),
      },
    ];
  }

  onPermissionSearch(params: SearchFormData): void {
    this.permissionSearchTerm.set(
      (params['permissionName'] || '').toString().trim(),
    );
  }

  onPermissionReset(): void {
    this.permissionSearchTerm.set('');
  }

  onSearch(params: any): void {
    // Clear tree selection mode flag
    this.isTreeSelectionMode.set(false);

    // Clear sidebar selection when search form is used
    this.clearSidebarSelection.set(true);

    this.searchParams.set({
      ...this.searchParams(),
      name: params.name || '',
      code: params.code || '',
      statusFlag: params.statusFlag,
      selectedTreeItem: '', // Clear selected tree item when searching
      page: 0,
    });

    // Call RoleStoreService search with the search parameters
    this.roleStoreService.searchRoles({
      name: params.name || '',
      code: params.code || '',
      statusFlag: params.statusFlag,
    });

    // Reset to parent only when using search form
    this.roleStoreService.resetToParentOnly();

    // Reset the clear signal after a short delay
    setTimeout(() => {
      this.clearSidebarSelection.set(false);
    }, 100);
  }

  onPageChange(event: any): void {
    const selectedRoleId = this.selectedRole()?.id;
    if (selectedRoleId && this.activeTabIndex() === 0) {
      // Pagination for child roles tab
      this.childRolesPage.set(event.page);
      this.childRolesSize.set(event.rows);
      this.loadChildRoles(
        selectedRoleId,
        event.page,
        event.rows,
        this.childRolesSearch(),
      );
    } else {
      // Pagination for main roles list
      this.searchParams.set({
        ...this.searchParams(),
        page: event.page,
        size: event.rows,
      });
    }
  }

  onAllRolesColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.allRolesTable) {
      this.allRolesTable.openColumnSelector(buttonElement);
    }
  }

  onChildRolesColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.childRolesTable) {
      this.childRolesTable.openColumnSelector(buttonElement);
    }
  }

  /**
   * Reset search form and data table to default state
   */
  onReset(): void {
    // Clear sidebar selection
    this.clearSidebarSelection.set(true);

    // Clear tree selection mode flag
    this.isTreeSelectionMode.set(false);

    // Reset search parameters to default values
    this.searchParams.set({
      name: '',
      code: '',
      statusFlag: undefined,
      selectedTreeItem: '', // Clear tree item selection
      page: 0,
      size: 20,
    });

    // Clear selected categories
    this.selectedRoles.set([]);
    // secondaryButton is a computed signal, so it will update automatically

    // Load all roles (reset to initial state)
    this.roleStoreService.searchRoles({
      name: '',
      code: '',
      statusFlag: undefined,
    });

    // Reset store to show parent only
    this.roleStoreService.resetToParentOnly();

    // Show success message
    this.messageService.add({
      severity: 'info',
      summary: this.translateService.instant('common.messages.success'),
      detail: this.translateService.instant(
        'configuration.roles.messages.reset_success',
      ),
    });

    // Reset the clear signal after a short delay
    setTimeout(() => {
      this.clearSidebarSelection.set(false);
    }, 100);
  }

  onSelectionChange(selection: ExtendedKeycloakGroupResponse[]): void {
    this.selectedRoles.set(selection);
  }

  onDelete(role: Pick<ExtendedKeycloakGroupResponse, 'name'>): void {
    if (!role?.name) {
      return;
    }

    this.selectedRoleForAction = {
      name: role.name,
    };
    this.currentAction = 'delete';
    this.dialogTitle = 'configuration.roles.dialog.confirm_delete';
    this.dialogMessage = this.translateService.instant(
      'configuration.roles.dialog.confirm_delete_message',
      { name: role.name || '' },
    );
    this.dialogButtons = [
      { label: 'common.actions.cancel', action: 'cancel', outlined: true },
      {
        label: 'common.actions.confirm',
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  onSidebarDelete(role: RoleSidebarModel): void {
    if (!role?.name) {
      return;
    }

    this.onDelete({
      name: role.name,
    });
  }

  onDeleteRoleDialogAction(action: string): void {
    if (action !== 'confirm') {
      this.closeConfirmationDialog();
      return;
    }

    if (!this.selectedRoleForAction?.name || this.deleteLoading()) return;
    // set loading on confirm button
    this.dialogButtons = this.dialogButtons.map((b) =>
      b.action === 'confirm' ? { ...b, loading: true } : b,
    );
    this.deleteLoading.set(true);
    this.rolesService.deleteRole(this.selectedRoleForAction.name).subscribe({
      next: () => {
        this.deleteLoading.set(false);
        this.dialogButtons = this.dialogButtons.map((b) =>
          b.action === 'confirm' ? { ...b, loading: false } : b,
        );
        this.showConfirmationDialog = false;
        this.selectedRoleForAction = null;
        this.currentAction = null;
        this.messageService.add({
          severity: 'success',
          summary: this.translateService.instant('common.messages.success'),
          detail: this.translateService.instant(
            'configuration.roles.messages.delete_role_success',
          ),
        });
        this.roleStoreService.refresh();
      },
      error: () => {
        this.deleteLoading.set(false);
        this.dialogButtons = this.dialogButtons.map((b) =>
          b.action === 'confirm' ? { ...b, loading: false } : b,
        );
        // Keep dialog open to allow retry
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.messages.error'),
          detail: this.translateService.instant(
            'configuration.roles.messages.delete_role_failed',
          ),
        });
      },
    });
  }

  getDeleteRoleMessage(): string {
    const name = this.selectedRoleForAction?.name || '';
    return this.translateService.instant(
      'configuration.roles.dialog.confirm_delete_message',
      { name },
    );
  }

  getRolesDisplayText(role: ExtendedKeycloakGroupResponse): string {
    if (!role) return '';

    return `employee.job_position.${role}`;
  }

  closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedRoleForAction = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  onAdd(): void {
    // Prefer a selected table row as parent; fallback to sidebar selection
    const tableSelection = this.selectedRoles();
    const tableParent =
      tableSelection && tableSelection.length > 0 ? tableSelection[0] : null;

    // If a table item is selected, update the store selection so breadcrumb/UI reflect it
    if (tableParent && tableParent.id) {
      this.roleStoreService.setSelectedSidebarItem({
        id: tableParent.id || '',
        name: tableParent.name || '',
        path: tableParent.path,
        parentId: tableParent.parentId,
      } as any);
    }

    const sidebarSelected = this.selectedRole();
    const parentId = tableParent?.id || sidebarSelected?.id || undefined;

    const headerKey = 'configuration.roles.dialog.create_role';
    const header = this.translateService.instant(headerKey);

    this.dialogRef = this.dialogService.open(AddRoleDialogComponent, {
      header,
      width: '600px',
      modal: true,
      closable: false,
      dismissableMask: false,
      closeOnEscape: false,
      focusOnShow: false,
      data: { parentId },
    });

    this.dialogRef.onClose.subscribe((result) => {
      this.dialogRef = null;
      if (result && typeof result.created === 'boolean') {
        if (result.created) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'configuration.roles.messages.create_role_success',
            ),
          });
          // Refresh list/tree
          this.roleStoreService.refresh();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'configuration.roles.messages.create_role_failed',
            ),
          });
        }
      }
    });
  }

  onMove(role: ExtendedKeycloakGroupResponse): void {}

  /**
   * Load child roles for the selected role
   */
  private loadChildRoles(
    groupId: string,
    page: number = 0,
    size: number = 20,
    search: string = '',
  ): void {
    if (!groupId) {
      this.childRoles.set([]);
      this.childRolesTotalRecords.set(0);
      return;
    }

    this.childRolesLoading.set(true);
    this.rolesService.getRoleChild(groupId, page, size, search).subscribe({
      next: ({ data, totalRecords }) => {
        const extendedRoles = data.map((role: any) => ({
          ...role,
          statusFlag: role?.statusFlag ?? false,
        }));
        this.childRoles.set(extendedRoles);
        this.childRolesTotalRecords.set(totalRecords);
        this.childRolesLoading.set(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.childRoles.set([]);
        this.childRolesTotalRecords.set(0);
        this.childRolesLoading.set(false);
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Handle tab change - load child roles when switching to tab 0
   */
  onTabChange(index: number): void {
    this.activeTabIndex.set(index);
    if (index === 0) {
      const selectedRoleId = this.selectedRole()?.id;
      if (selectedRoleId) {
        this.childRolesPage.set(0);
        this.loadChildRoles(
          selectedRoleId,
          0,
          this.rows,
          this.childRolesSearch(),
        );
      }
    }
  }

  /**
   * Handle search for child roles tab
   */
  onChildRolesSearch(params: any): void {
    const searchValue = (params?.search || '').toString();
    this.childRolesSearch.set(searchValue);
    this.childRolesPage.set(0);
    const selectedRoleId = this.selectedRole()?.id;
    if (selectedRoleId) {
      this.loadChildRoles(selectedRoleId, 0, this.rows, searchValue);
    }
  }

  /**
   * Handle reset for child roles search
   */
  onChildRolesReset(): void {
    this.childRolesSearch.set('');
    this.childRolesPage.set(0);
    const selectedRoleId = this.selectedRole()?.id;
    if (selectedRoleId) {
      this.loadChildRoles(selectedRoleId, 0, this.rows, '');
    }
  }
}
